import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import {
  ConversationHookResult,
  ConversationData,
  processConversationMessages,
  createEnrichedConversation,
} from "@/utils/conversationUtils";

export const useFetchSpecificInternalConversation = (
  conversationId: string | null,
  agentId?: string,
): ConversationHookResult => {
  const { user } = useAuth();
  const [conversation, setConversation] = useState<ConversationData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [initialMessages, setInitialMessages] = useState<any[]>([]);
  const [previousResponseId, setPreviousResponseId] = useState<string | null>(null);

  const fetchSpecificInternalConversation = async () => {
    if (!user?.id || !agentId || !conversationId) {
      setIsLoading(false);
      return;
    }

    try {
      // Fetch the specific conversation
      const { data: conversationData, error: conversationError } = await supabase
        .from("conversations")
        .select(`
          *,
          messages (
            id,
            sender_type,
            content,
            created_at,
            metadata,
            openai_message_id
          ),
          agent:agents (*)
        `)
        .eq("id", conversationId)
        .eq("agent_id", agentId)
        .eq("user_id", user.id)
        .is("agent_chat_user_id", null) // Ensure it's an internal conversation
        .order("created_at", { referencedTable: "messages", ascending: true })
        .single();

      if (conversationError) {
        throw conversationError;
      }

      if (!conversationData) {
        setError("Conversation not found");
        setIsLoading(false);
        return;
      }

      // Process messages using shared utility
      const { messages, previousResponseId: responseId } = processConversationMessages(
        conversationData.messages
      );

      // Create enriched conversation using shared utility
      const enrichedConversation = createEnrichedConversation(conversationData, responseId);

      setConversation(enrichedConversation);
      setInitialMessages(messages);
      setPreviousResponseId(responseId);
    } catch (error) {
      console.error("Error fetching conversation:", error);
      setError(error instanceof Error ? error.message : "Failed to load conversation");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!user?.id || !agentId || !conversationId) {
      // Clear stale data when parameters are missing
      setConversation(null);
      setInitialMessages([]);
      setPreviousResponseId(null);
      setError(null);
      setIsLoading(false);
      return;
    }
    
    fetchSpecificInternalConversation();
  }, [user?.id, agentId, conversationId]);

  return {
    conversation,
    isLoading,
    error,
    conversationId: conversation?.id || null,
    initialMessages,
    previousResponseId,
  };
};
